import * as constants from '../constants';
import * as types from '../types';
import { formatLogMessage, mergeContext, pruneContext } from '../util';

import { Strategy, type StrategyOptions } from './Strategy';

export abstract class AbstractConsoleStrategy<
  O extends StrategyOptions = StrategyOptions,
> extends Strategy<O> {
  /**
   * Logs the provided information via the underlying {@link console} object that the strategy
   * is configured to use.  This method should not be called directly, but should instead be called
   * via the {@link logWithStrategy} method.
   */
  protected log(
    logger: types.ILoggerConfiguration,
    level: types.LogLevel,
    params: { readonly time?: number } & types.ParsedArgs,
  ): void {
    const writer = constants.ConsoleWriters[level];

    const message = formatLogMessage({ level, message: params.message, time: params.time }, logger);

    const ctx = pruneContext(params.context);
    if (ctx) {
      return writer(message, mergeContext(ctx, { error: params.error }));
    }
    if (params.error) {
      return writer(message, params.error);
    }
    if (message) {
      writer(message);
    }
  }
}

/**
 * A logger strategy, {@link types.IStrategy}, that emits the underlying log messages, context,
 * {@link types.LogContext}, and in some cases, error, {@link types.LoggerCustomError}, via the
 * traditional {@link console} object.
 *
 * This strategy can be used in all runtime environments, but should not be used in conjunction with
 * the {@link PinoStrategy} strategy because the {@link PinoStrategy} strategy will also indirectly
 * log via the {@link console} object in both server/client contexts.
 */
export class ConsoleStrategy extends AbstractConsoleStrategy {
  public strategyId: types.StrategyId = types.StrategyId.Pino;
}
